import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { getCurrentUser } from '../../lib/auth';
import toast from 'react-hot-toast';
import { FaPlay, FaHeart, FaComment, FaEllipsisV, FaChartLine, FaDollarSign, FaUnlock, FaTimes, FaTrash } from 'react-icons/fa';
import PromoteVideoModal from '../../promoted/components/PromoteVideoModal';

interface Video {
  id: string;
  video_url: string;
  thumbnail_url: string | null;
  title: string;
  likes_count: number;
  comments_count: number;
  is_paid: boolean;
  is_promoted: boolean;
  price: number;
}

interface VideoGridProps {
  videos: Video[];
  loading?: boolean;
}

const VideoGrid: React.FC<VideoGridProps> = ({ videos, loading = false }) => {
  const navigate = useNavigate();
  const videoRefs = useRef<{ [key: string]: HTMLVideoElement }>({});
  const [activeMenu, setActiveMenu] = useState<string | null>(null);
  const [showPromoteModal, setShowPromoteModal] = useState(false);
  const [selectedVideoId, setSelectedVideoId] = useState<string | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState<string | null>(null);
  const user = getCurrentUser();

  useEffect(() => {
    videos.forEach(video => {
      if (!video.thumbnail_url && video.video_url) {
        generateThumbnail(video.id, video.video_url);
      }
    });
  }, [videos]);

  const generateThumbnail = async (videoId: string, videoUrl: string) => {
    try {
      const video = document.createElement('video');
      video.crossOrigin = 'anonymous';
      video.src = videoUrl;

      await new Promise((resolve, reject) => {
        video.onloadedmetadata = resolve;
        video.onerror = reject;
        video.load();
      });

      video.currentTime = video.duration * 0.25;

      await new Promise((resolve) => {
        video.onseeked = resolve;
      });

      const canvas = document.createElement('canvas');
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      const ctx = canvas.getContext('2d');
      if (!ctx) throw new Error('Failed to get canvas context');

      ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

      const blob = await new Promise<Blob>((resolve) => {
        canvas.toBlob((b) => resolve(b!), 'image/jpeg', 0.9);
      });

      const thumbnailFile = new File([blob], `${videoId}-thumbnail.jpg`, {
        type: 'image/jpeg'
      });

      const { data: uploadData, error: uploadError } = await supabase.storage
        .from('Public')
        .upload(`thumbnails/${videoId}.jpg`, thumbnailFile, {
          cacheControl: '3600',
          upsert: true
        });

      if (uploadError) throw uploadError;

      const { data: { publicUrl } } = supabase.storage
        .from('Public')
        .getPublicUrl(`thumbnails/${videoId}.jpg`);

      const { error: updateError } = await supabase
        .from('ponavideos')
        .update({ thumbnail_url: publicUrl })
        .eq('id', videoId);

      if (updateError) throw updateError;

    } catch (error) {
      console.error('Error generating thumbnail:', error);
    }
  };

  const handleDeleteVideo = async (videoId: string) => {
    try {
      // Delete video from storage
      const { error: storageError } = await supabase.storage
        .from('videospono')
        .remove([`${user?.id}/${videoId}.mp4`]);

      if (storageError) throw storageError;

      // Delete video record from database
      const { error: dbError } = await supabase
        .from('ponavideos')
        .delete()
        .eq('id', videoId);

      if (dbError) throw dbError;

      toast.success('Video deleted successfully');
      // Refresh the page to update the video list
      window.location.reload();
    } catch (error: any) {
      console.error('Error deleting video:', error);
      toast.error(error.message || 'Failed to delete video');
    } finally {
      setShowDeleteConfirm(null);
      setActiveMenu(null);
    }
  };

  if (loading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {[...Array(6)].map((_, i) => (
          <div key={i} className="aspect-video bg-gray-800 animate-pulse rounded-lg" />
        ))}
      </div>
    );
  }

  if (videos.length === 0) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-400">No videos found</p>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
      {videos.map((video) => (
        <div
          key={video.id}
          className="relative group"
        >
          {/* Video Preview */}
          <div 
            className="aspect-video rounded-lg overflow-hidden bg-gray-900 cursor-pointer"
            onClick={() => navigate(`/video/${video.id}`)}
          >
            {video.thumbnail_url ? (
              <img
                src={video.thumbnail_url}
                alt={video.title}
                className="w-full h-full object-cover"
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center">
                <div className="animate-pulse text-gray-500">
                  Generating thumbnail...
                </div>
              </div>
            )}
            <div className="absolute inset-0 bg-black bg-opacity-50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
              <FaPlay className="w-12 h-12 text-white" />
            </div>
          </div>
          
          {/* Video Info */}
          <div className="mt-2 flex justify-between items-start">
            <div>
              <h3 className="text-white font-medium truncate">{video.title}</h3>
              <div className="flex items-center space-x-4 mt-1 text-sm text-gray-400">
                <div className="flex items-center">
                  <FaHeart className="w-4 h-4 mr-1" />
                  {video.likes_count}
                </div>
                <div className="flex items-center">
                  <FaComment className="w-4 h-4 mr-1" />
                  {video.comments_count}
                </div>
              </div>
            </div>

            {/* Menu Button */}
            <div className="relative">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setActiveMenu(activeMenu === video.id ? null : video.id);
                }}
                className="p-2 text-gray-400 hover:text-white rounded-full hover:bg-gray-800"
              >
                <FaEllipsisV className="w-4 h-4" />
              </button>

              {/* Menu Dropdown */}
              {activeMenu === video.id && (
                <div className="absolute right-0 mt-2 w-48 bg-gray-900 rounded-lg shadow-lg py-1 z-10">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setShowDeleteConfirm(video.id);
                    }}
                    className="flex items-center w-full px-4 py-2 text-sm text-red-400 hover:bg-gray-800"
                  >
                    <FaTrash className="w-4 h-4 mr-3" />
                    Delete Video
                  </button>
                </div>
              )}

              {/* Delete Confirmation Modal */}
              {showDeleteConfirm === video.id && (
                <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50">
                  <div className="bg-gray-900 rounded-lg p-6 max-w-sm w-full mx-4">
                    <h3 className="text-xl font-semibold text-white mb-4">Delete Video?</h3>
                    <p className="text-gray-400 mb-6">
                      Are you sure you want to delete this video? This action cannot be undone.
                    </p>
                    <div className="flex justify-end space-x-4">
                      <button
                        onClick={() => setShowDeleteConfirm(null)}
                        className="px-4 py-2 text-gray-400 hover:text-white"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={() => handleDeleteVideo(video.id)}
                        className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Status Badges */}
          <div className="absolute top-2 left-2 flex space-x-2">
            {video.is_promoted && (
              <span className="px-2 py-1 bg-yellow-500 text-black text-xs font-medium rounded-full">
                Promoted
              </span>
            )}
            {video.is_paid && (
              <span className="px-2 py-1 bg-red-500 text-white text-xs font-medium rounded-full">
                ${video.price}
              </span>
            )}
          </div>
        </div>
      ))}
    </div>
  );
};

export default VideoGrid;